import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
} from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { PeriodLength } from '@helios/state-management'
import { IntegerIdValidator } from '@helios/validators'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { DisplayDateFormat } from '@northfork/basics'
import { of, Subject } from 'rxjs'
import { debounceTime, finalize, switchMap, takeUntil } from 'rxjs/operators'
import {
  HttpAccountsService,
  LIBDetails,
} from '../../../services/http-accounts-service/http-accounts.service'

export interface CreateNewSessionModalResponse {
  /**
   * Account ID that the user wants a session created for.
   */
  accountIds: number[]
  reconDate: Date
  periodLength: PeriodLength
  periodLengthNDays?: number
  periodLengthNFromDate?: Date
  /**
   * Flag to override the autoclose session if account if found in current sessions
   */
  overrideAutoclose: boolean
  selectedBasis: string
}

@Component({
  selector: 'hs-create-new-session-modal',
  templateUrl: './create-new-session-modal.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateNewSessionModalComponent
  implements AfterViewInit, OnDestroy
{
  accountIds = new FormControl(null, [IntegerIdValidator])
  reconDate = new FormControl(null, [Validators.required])
  periodLength = new FormControl(PeriodLength.Last100Days, [
    Validators.required,
  ])
  selectedBasis = new FormControl()
  fromDate = new FormControl()

  form = new FormGroup({
    accountId: this.accountIds,
    reconDate: this.reconDate,
    periodLength: this.periodLength,
    fromDate: this.fromDate,
  })

  dateFormat = DisplayDateFormat
  labelLIB = 'Checking Account is LIB ..'
  windowSizes: PeriodLength[] = Object.values(PeriodLength)

  FromDate = PeriodLength.FromDate

  private readonly destroySubject$ = new Subject<void>()

  submitDisabled = true
  accountBasis: string[] = []
  checkingLIB = false
  isLIBAccount = false

  constructor(
    public activeModal: NgbActiveModal,
    public cd: ChangeDetectorRef,
    private readonly httpAccountService: HttpAccountsService,
  ) {}

  ngAfterViewInit() {
    this.selectedBasis.setValue(undefined)

    this.form.valueChanges
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(() => {
        this.setSubmitDisabled()
      })
    this.accountIds.valueChanges
      .pipe(
        debounceTime(500), // Wait for a pause in events
        switchMap((accountId: number) => {
          if (accountId) {
            return this.fetchBasisForLib(accountId).pipe(
              finalize(() => {
                this.checkingLIB = false // Reset loading state after completing the call
              }),
            )
          }
          // Handle empty accountId case
          this.resetFlags()
          return of(null) // Return null observable if no ID is present
        }),
        takeUntil(this.destroySubject$), // Prevent memory leaks
      )
      .subscribe(libDetails => {
        if (libDetails) this.updateBasisFromLibDetails(libDetails)
      })
    if (this.accountIds.value && this.isLIBAccount)
      // helps not load data if no accountId or account id is a non lib account
      this.accountIds.updateValueAndValidity()
    this.setSubmitDisabled()
  }

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  createSession(overrideAutoclose: boolean) {
    if (this.submitDisabled) return
    this.activeModal.close({
      accountIds: [this.accountIds.value],
      reconDate: this.reconDate.value,
      periodLength: this.periodLength.value,
      periodLengthNFromDate: this.fromDate.value,
      selectedBasis: this.selectedBasis.value,
      overrideAutoclose: overrideAutoclose,
    } as CreateNewSessionModalResponse)
  }

  /** @internal */
  setSubmitDisabled() {
    this.submitDisabled = this.isSubmitDisabled()
  }

  private isSubmitDisabled() {
    if (this.reconDate.value == null) return true
    if (this.periodLength.value == null) return true
    if (
      this.periodLength.value === PeriodLength.FromDate &&
      this.fromDate.value == null
    )
      return true
    return false
  }

  fetchBasisForLib(accountId: number) {
    this.checkingLIB = true // Set loading state
    return this.httpAccountService.getLIBDetails(accountId)
  }

  updateBasisFromLibDetails(libDetails: LIBDetails) {
    if (libDetails.isLIB) {
      this.isLIBAccount = true // Set LIB state to true
      this.accountBasis = libDetails.basisDetails.ACCOUNT_BASES.map(
        base => base.prefix,
      )

      // Set the default basis
      const defaultBasis = libDetails.basisDetails.ACCOUNT_BASES.find(
        basis => basis.basisId === libDetails.basisDetails.DEFAULT_BASIS_ID,
      )?.prefix

      if (defaultBasis) {
        this.selectedBasis.setValue(defaultBasis) // Set the selected basis only if valid
      }
    } else {
      this.resetFlags() // Call to reset flags if not LIB
    }
  }

  resetFlags() {
    this.isLIBAccount = false
    this.selectedBasis.setValue(undefined) // Clear selected basis
  }
}
